/**
 * Main -- Bootstrap entry point for Nexo Panel v2.2.
 * Wires auth, router, and views. Validates session on startup.
 *
 * @module main
 */

import { AppConfig } from './config.js';
import { reusarRutas, ApiBase } from './infrastructure/api/ApiBase.js';
import { reglasDeReusoDelDashboard } from './presentation/components/reusoDelDashboard.js';
import { Router } from './router.js';
import { AuthService } from './application/AuthService.js';
import { setupAuthGate } from './presentation/components/AuthGate.js';
import { setupNavBar } from './presentation/components/NavBar.js';
import { tarjetaAprobaciones, conteoEnPalabras } from './presentation/components/menuDelPanel.js';
import { renderTaskList } from './presentation/views/TaskListView.js';
import { renderMissionList } from './presentation/views/MissionListView.js';
import { renderMissionDetail } from './presentation/views/MissionDetailView.js';
import { renderInstructionList } from './presentation/views/InstructionListView.js';
import { renderInstructionDetail } from './presentation/views/InstructionDetailView.js';
import { renderProjectSelector } from './presentation/views/ProjectSelectorView.js';
import { renderProjectDetail }   from './presentation/views/ProjectDetailView.js';
import { renderUserList } from './presentation/views/UserListView.js';
import { renderPersonasMaster } from './presentation/views/PersonasMasterView.js';
import { renderFichaPersona } from './presentation/views/FichaPersonaView.js';
import { renderFichaAgente } from './presentation/views/FichaAgenteView.js';
import { renderAgentList } from './presentation/views/AgentListView.js';
import { renderAgentsDashboard } from './presentation/views/AgentsDashboardView.js';
import { renderInicioMaderinco } from './presentation/views/InicioMaderincoView.js';
import { esInicioDeMaderinco } from './application/presupuestos/inicioMaderinco.js';
import { getSelectedBusiness as negocioDelInicio } from './infrastructure/negocioElegido.js';
import { renderSalud } from './presentation/views/SaludView.js';
import { renderContexto } from './presentation/views/ContextoView.js';
import { renderOAuthAutorizar } from './presentation/views/OAuthAutorizarView.js';
import { renderJosseCola } from './presentation/views/JosseColaView.js';
import { JosseColaApiClient } from './infrastructure/api/JosseColaApiClient.js';
import { leerDecision, pendientesReales } from './application/decisiones/tableroDecisiones.js';
import { SaludApiClient } from './infrastructure/api/SaludApiClient.js';
import { promesaDelDia } from './application/salud/promesaDelDia.js';
import { renderDanosColaborador } from './presentation/views/DanosColaboradorView.js';
import { renderPensum } from './presentation/views/PensumView.js';
import { renderFuncionalidades } from './presentation/views/FuncionalidadesView.js';
import { renderItemEstandar } from './presentation/views/ItemEstandarView.js';
import { renderArbol } from './presentation/views/ArbolView.js';
import { renderNotificaciones, resumenPorAprobar } from './presentation/views/NotificacionesView.js';
import { renderMovimientosKiosk } from './presentation/views/MovimientosKioskView.js';
import { renderBancoNxs } from './presentation/views/BancoNxsView.js';
import { renderNegociosList } from './presentation/views/NegociosListView.js';
import { renderNegocioDetail } from './presentation/views/NegocioDetailView.js';
import { renderNegocioNuevoWizard } from './presentation/views/NegocioNuevoWizardView.js';
import { renderOnboardPublic } from './presentation/views/OnboardPublicView.js';
import { renderColaboradores } from './presentation/views/ColaboradoresView.js';
import { renderAsistencia } from './presentation/views/AsistenciaView.js';
import { renderAsistenciaAudit } from './presentation/views/AsistenciaAuditView.js';
import { renderMarcacion } from './presentation/views/MarcacionView.js';
import { HorariosView } from './presentation/views/HorariosView.js';
const renderHorarios = (c) => { c.appendChild(HorariosView()); };
import { renderSystemInfo }    from './presentation/views/SystemInfoView.js';
import { renderDesktopAdmin } from './presentation/views/DesktopAdminView.js';
import { AuditoriaView } from './presentation/views/AuditoriaView.js';
const renderAuditoria = (c) => { c.appendChild(AuditoriaView()); };
import { PublicidadView } from './presentation/views/PublicidadView.js';
const renderPublicidad = (c) => { c.appendChild(PublicidadView()); };
import { NotesView } from './presentation/views/NotesView.js';
const renderNotes = (c) => { c.appendChild(NotesView()); };
import { renderKnowledge } from './presentation/views/KnowledgeView.js';
import { renderItemList } from './presentation/views/ItemListView.js';
import { ARTIFACT_ROLES } from './domain/entities/Item.js';
import { resolveUserAudience, audienceMatches } from './presentation/shared/useAudience.js';

// PANEL-ARTIFACTS-AUDIENCE-V1-001: artefactos INTERNOS de Nexo Digital. audience='nexo' ->
// ocultos para audiencias business/client (cliente ve panel limpio). El resto default 'both'
// (visible para todos). dev/admin/manager-nexo resuelven 'all' y ven todo.
const ARTIFACT_AUDIENCE = {
  '#/missions': 'nexo', '#/tasks': 'nexo', '#/agents': 'nexo', '#/agents-dashboard': 'nexo', '#/salud': 'nexo', '#/danos-colaborador': 'nexo',
  '#/instructions': 'nexo', '#/skills': 'nexo', '#/items': 'nexo', '#/projects': 'nexo',
  '#/knowledge': 'nexo', '#/banco-tokens': 'nexo', '#/notas': 'nexo', '#/users': 'nexo',
  '#/desktop-admin': 'nexo', '#/personas': 'nexo', '#/agente': 'nexo',
  '#/contexto': 'nexo', '#/decisiones': 'nexo', '#/pensum': 'nexo', '#/funcionalidades': 'nexo', '#/arbol': 'nexo',
};
import { renderCapacitacion } from './presentation/views/CapacitacionView.js';
import { renderSkills } from './presentation/views/SkillsView.js';
import { renderAgentQuality } from './presentation/views/AgentQualityView.js';
import { renderInventario } from './presentation/views/InventarioView.js';
import { renderCarrito } from './presentation/views/CarritoView.js';
import { renderPresupuestos } from './presentation/views/PresupuestosView.js';
import { renderEquiposCatalog } from './presentation/views/EquiposCatalogView.js';
import { renderIngresos } from './presentation/views/IngresosView.js';
import { renderCompras } from './presentation/views/ComprasView.js';
import { renderArgemiroKiosk } from './presentation/views/ArgemiroKioskView.js';
import { renderEgresos } from './presentation/views/EgresosView.js';
import { renderCreditos } from './presentation/views/CreditosView.js';
import { renderSaldos } from './presentation/views/SaldosView.js';
import { renderInformes } from './presentation/views/InformesView.js';
import { renderNexoPagos } from './presentation/views/NexoPagosView.js';
import { setupBusinessSelector } from './presentation/components/BusinessSelector.js';
import { setupFloatingNotesButton } from './presentation/components/NxFloatingButton.js';
import { setupOperadorPin } from './presentation/components/OperadorPin.js';
import { consumePendingToast } from './application/RewardService.js';
import { iniciarAvisoVersionNueva, recargarSiFallaUnModulo } from './presentation/components/AvisoVersionNueva.js';
import { etapaDeSesion } from './application/inventario/estadosDelConteo.js';

const $gate = document.getElementById('nx-auth-gate');
const $app = document.getElementById('nx-app');
const $main = document.getElementById('nx-main');

function showGate() {
  $gate.hidden = false;
  $app.hidden = true;
  setupAuthGate(() => showApp());
}

// Devuelve true si el usuario logueado es de un negocio (login con businessId lockeado),
// cubre role=client y role=manager con nx_business_locked=true. Excluye dev/admin.
function isBusinessUser() {
  const locked = localStorage.getItem('nx_business_locked') === 'true';
  const role = AppConfig.role;
  return locked && role !== 'dev' && role !== 'admin';
}

function showApp() {
  $gate.hidden = true;
  $app.hidden = false;
  // Vista negocio: clase en body para ocultar navbar brand/links/selector/widgets via CSS
  if (isBusinessUser()) {
    document.body.classList.add('role-client');
  } else {
    document.body.classList.remove('role-client');
  }
  setupNavBar(() => showGate());
  setupBusinessSelector();
  setupOperadorPin();
  setupFloatingNotesButton(); // FAB global para crear notas desde cualquier vista — PANEL-NOTES-V1-001 T4
  // PANEL-VERSION-NUEVA-T1: avisa (sin recargar) cuando el servidor ya sirve otro commit.
  // Solo en el panel autenticado: la ficha publica del onboarding entra por showPublic().
  iniciarAvisoVersionNueva();
  recargarSiFallaUnModulo(); // VISTA-VIEJA-DEPLOY-T1: un modulo de un build que ya no esta = recargar una vez
  router.start();
  // Show queued NXS toasts (survives page reload)
  setTimeout(() => {
    const pending = consumePendingToast();
    if (pending) {
      const totalNxs = pending.missions.reduce((s, m) => s + (m.nxs || 0), 0);
      const names = pending.missions.map(m => m.title).join(', ');
      const toast = document.createElement('div');
      toast.className = 'reward-toast';
      if (pending.type === 'revert') toast.style.borderColor = '#f85149';
      const sign = pending.type === 'revert' ? '-' : '+';
      const color = pending.type === 'revert' ? '#f85149' : '';
      toast.innerHTML = `<strong>${pending.type === 'revert' ? 'Mision revertida' : 'Mision completada!'}</strong><br>${names}<br><span class="nxs-earned"${color ? ` style="color:${color}"` : ''}>${sign}${totalNxs} NXS</span>`;
      document.body.appendChild(toast);
      requestAnimationFrame(() => toast.classList.add('visible'));
      setTimeout(() => { toast.classList.remove('visible'); setTimeout(() => toast.remove(), 400); }, 5000);
    }
  }, 500);
}

reusarRutas(reglasDeReusoDelDashboard());


async function renderDashboard(container) {
  const role = AppConfig.role;

  // MAD-DASHBOARD-ESTILO-T1 (SI de Josse #44147): el negocio de Maderinco entra a "la mesa del dia" (sus presupuestos),
  // no al dashboard ni a la tablet de restaurante. Los demas negocios y los colaboradores (client) siguen igual.
  if (esInicioDeMaderinco(negocioDelInicio(), role)) {
    renderInicioMaderinco(container);
    return;
  }

  // Vista limpia para usuarios de negocio (login con businessId -> nx_business_locked).
  // Cubre role=client y role=manager con business bloqueado. Excluye dev/admin para no
  // limitar acceso a admins del sistema que podrian estar revisando un negocio.
  if (isBusinessUser()) {
    renderBusinessDashboard(container);
    return;
  }

  const allowed = ARTIFACT_ROLES[role];
  // PANEL-ARTIFACTS-AUDIENCE-V1-001: además del guard por rol, ocultar artefactos internos
  // (audience='nexo') a audiencias business/client. Default 'both' = visible para todos.
  const userAudience = resolveUserAudience();
  const show = (route) =>
    (!allowed || allowed.includes(route)) &&
    audienceMatches(ARTIFACT_AUDIENCE[route] || 'both', userAudience);

  container.innerHTML = `
    <section class="dashboard">
      <h1>Dashboard</h1>
      <p class="subtitle">Bienvenido a Nexo Panel v2.2
        <span id="nx-dash-skill-nxs-chip" class="task-tag" style="margin-left:10px;background:#d2992222;color:#d29922;font-size:0.875rem;vertical-align:middle">NXS skills: ...</span>
      </p>
      <div id="nx-tablero-dueno"></div>
      <!-- INICIO-CLARIDAD (FRONT-AUDITORIA-VISTAS-T1, SI #30130): el negocio del mes va ANTES de los accesos. Medido en
           2.2.628 a 1366: los 16 accesos llenaban la primera pantalla y "Tu negocio este mes" quedaba bajo el pliegue. -->
      <div id="nx-como-nos-fue"></div>
      <div id="nx-tiempos-pedido"></div>
      <div id="nx-tarjetas-negocio"></div>
      <div id="nx-indicadores-mes"></div>
      <div class="dash-section-title" data-dash-accesos-titulo>Accesos</div>
      <div class="dashboard-grid dash-accesos">
        ${show('#/tasks') ? '<a href="#/tasks" class="dash-card"><span class="dash-icon">&#9745;</span><span class="dash-label">Tareas</span><span class="dash-count" id="dc-tasks">...</span></a>' : ''}
        ${show('#/missions') ? '<a href="#/missions" class="dash-card"><span class="dash-icon">&#11088;</span><span class="dash-label">Misiones</span><span class="dash-count" id="dc-missions">...</span></a>' : ''}
        ${show('#/instructions') ? '<a href="#/instructions" class="dash-card"><span class="dash-icon">&#128218;</span><span class="dash-label">Instrucciones</span><span class="dash-count" id="dc-instructions">...</span></a>' : ''}
        ${show('#/projects') ? '<a href="#/projects" class="dash-card"><span class="dash-icon">&#128194;</span><span class="dash-label">Proyectos</span><span class="dash-count" id="dc-projects">...</span></a>' : ''}
        ${show('#/users') ? '<a href="#/users" class="dash-card"><span class="dash-icon">&#128100;</span><span class="dash-label">Usuarios</span><span class="dash-count" id="dc-users">...</span></a>' : ''}
        ${show('#/agents') ? '<a href="#/agents" class="dash-card"><span class="dash-icon">&#129302;</span><span class="dash-label">Agentes</span><span class="dash-count" id="dc-agents">...</span></a>' : ''}
        ${show('#/agents-dashboard') ? '<a href="#/agents-dashboard" class="dash-card"><span class="dash-icon">&#128101;</span><span class="dash-label">Equipo Argys</span><span class="dash-count">Live</span></a>' : ''}
        ${show('#/salud') ? '<a href="#/salud" class="dash-card"><span class="dash-icon">&#9881;&#65039;</span><span class="dash-label">Sala de Maquinas</span><span class="dash-count">Salud</span></a>' : ''}
        ${show('#/contexto') ? '<a href="#/contexto" class="dash-card"><span class="dash-icon">&#128273;</span><span class="dash-label">Accesos de asistentes</span><span class="dash-count">Contexto</span></a>' : ''}
        ${tarjetaAprobaciones({ tablet: false, role, permitida: show('#/notificaciones') })}
        ${show('#/decisiones') ? '<a href="#/decisiones" class="dash-card"><span class="dash-icon">&#128220;</span><span class="dash-label">Lo que necesita de vos</span><span class="dash-count" data-dash-decisiones-conteo>Decisiones</span></a>' : ''}
        ${show('#/danos-colaborador') ? '<a href="#/danos-colaborador" class="dash-card"><span class="dash-icon">&#9878;&#65039;</span><span class="dash-label">Danos por colaborador</span><span class="dash-count">Inventario</span></a>' : ''}
        ${show('#/saldos') ? '<a href="#/saldos" class="dash-card"><span class="dash-icon">&#128202;</span><span class="dash-label">Saldos</span><span class="dash-count">Resumen</span></a>' : ''}
        ${show('#/ingresos') ? '<a href="#/ingresos" class="dash-card"><span class="dash-icon">&#128176;</span><span class="dash-label">Ingresos</span><span class="dash-count">Ventas</span></a>' : ''}
        ${show('#/egresos') ? '<a href="#/egresos" class="dash-card"><span class="dash-icon">&#128184;</span><span class="dash-label">Egresos</span><span class="dash-count">Salidas</span></a>' : ''}
        ${show('#/creditos') ? '<a href="#/creditos" class="dash-card"><span class="dash-icon">&#128210;</span><span class="dash-label">Creditos</span><span class="dash-count">Cliente</span></a>' : ''}
        <a href="#/banco" class="dash-card"><span class="dash-icon">&#127974;</span><span class="dash-label">Banco NXS</span><span class="dash-count" id="dc-nxs">...</span></a>
        <a href="#/negocios" class="dash-card"><span class="dash-icon">&#127970;</span><span class="dash-label">Negocios</span><span class="dash-count" id="dc-negocios">...</span></a>
        ${show('#/colaboradores') ? '<a href="#/colaboradores" class="dash-card"><span class="dash-icon">&#128101;</span><span class="dash-label">Colaboradores</span><span class="dash-count" id="dc-colaboradores">--</span></a>' : ''}
        ${show('#/horarios') ? '<a href="#/horarios" class="dash-card"><span class="dash-icon">&#128197;</span><span class="dash-label">Horarios</span><span class="dash-count" id="dc-horarios">—</span></a>' : ''}
        <a href="#/asistencia" class="dash-card"><span class="dash-icon">&#128336;</span><span class="dash-label">Asistencia</span><span class="dash-count" id="dc-asistencia">--</span></a>
        <a href="#/capacitacion" class="dash-card"><span class="dash-icon">&#127891;</span><span class="dash-label">Capacitacion</span><span class="dash-count" id="dc-learning">...</span></a>
        ${show('#/skills') ? '<a href="#/skills" class="dash-card"><span class="dash-icon">&#127942;</span><span class="dash-label">Skills</span><span class="dash-count" id="dc-skills">...</span></a>' : ''}
        ${show('#/publicidad') ? '<a href="#/publicidad" class="dash-card"><span class="dash-icon">&#128247;</span><span class="dash-label">Publicidad</span><span class="dash-count">Generar</span></a>' : ''}
        ${show('#/auditoria') ? '<a href="#/auditoria" class="dash-card"><span class="dash-icon">&#128269;</span><span class="dash-label">Auditoria</span><span class="dash-count">OCR</span></a>' : ''}
        ${show('#/items') ? '<a href="#/items" class="dash-card"><span class="dash-icon">&#128230;</span><span class="dash-label">Items</span><span class="dash-count">34 items</span></a>' : ''}
        ${show('#/knowledge') ? '<a href="#/knowledge" class="dash-card"><span class="dash-icon">&#128218;</span><span class="dash-label">Conocimiento</span><span class="dash-count">RAG</span></a>' : ''}
        ${show('#/inventario') ? '<a href="#/inventario" class="dash-card"><span class="dash-icon">&#128203;</span><span class="dash-label">Inventario</span><span class="dash-count">Sync Desktop</span></a>' : ''}
        ${show('#/carrito') ? '<a href="#/carrito" class="dash-card"><span class="dash-icon">&#128722;</span><span class="dash-label">Carrito</span><span class="dash-count">Ventas</span></a>' : ''}
        ${show('#/desktop-admin') ? '<a href="#/desktop-admin" class="dash-card"><span class="dash-icon">&#128187;</span><span class="dash-label">Nexo Desktop</span><span class="dash-count" id="dc-desktop">—</span></a>' : ''}
      </div>
      ${show('#/salud') ? '<div data-promesa-dia hidden></div>' : ''}
      <div id="nx-nxs-widget"></div>
      <div id="nx-week-kpis"></div>
      <div class="dash-info">
        <p>Entorno: <strong>${AppConfig.env}</strong> &middot; API: <code>${AppConfig.apiBase || 'proxy'}</code></p>
      </div>
      <div id="nx-fab-root"></div>
    </section>`;

  loadDashCounts();
  // PANEL-CONTADORES-VIVOS-T1: al volver a la pestana, los numeros de Accesos y Aprobaciones se leen otra vez.
  import('./application/dashboard/contadoresVivos.js').then(({ refrescarAlVolver }) => refrescarAlVolver({
    doc: document,
    estaEnPantalla: () => !!document.getElementById('dc-tasks') || !!document.querySelector('[data-dash-aprobaciones-conteo]'),
    olvidar: () => ApiBase.olvidarReusados(),
    refrescar: () => {
      const $vista = document.querySelector('section.dashboard');
      if (document.getElementById('dc-tasks')) loadDashCounts();
      if ($vista) pintarConteoAprobaciones($vista);
    }
  }));
  pintarConteoAprobaciones(container);
  pintarConteoDecisiones(container);
  pintarPromesaDelDia(container);
  loadNxsCount();
  loadLearningNxsSkillsBadge();
  loadNegociosCount();
  loadDesktopCount();
  loadLearningQuestionsCount();
  loadSkillsCount();
  import('./presentation/components/NxsWidget.js').then(m => m.renderNxsWidget('nx-nxs-widget'));
  // SUJ-T3 (ASSIGN #17005, CORRECCION #17094): las tres tarjetas del mes del negocio elegido.
  // Gate de rol EXPLICITO: dueno (manager) y admin de plataforma (admin/dev). No va en
  // renderBusinessDashboard: esa es la tablet de los colaboradores (decision Josse 12-ago).
  if (['manager', 'admin', 'dev'].includes(role)) {
    // PANEL-ASIS-HOY-T1 (Ares #26189): Asistencia y Danos del mes desde la API (la misma cifra que el MCP). Mismo gate
    // que las tarjetas: dueno y admin, nunca la tablet. Sin el GET de A no pinta nada.
    // UN NUMERO POR PANTALLA (Q #26213): primero se pregunta a la API; si pinto, las tarjetas de antes salen sin
    // asistencia ni danos del mes (otra definicion). Si no, quedan como siempre. En orden, sin carrera.
    Promise.all([import('./presentation/shared/NxIndicadoresMes.js'), import('./presentation/shared/NxIndicadoresDueno.js')])
      .then(async ([mes, dueno]) => {
        let pinto = false;
        try { pinto = await mes.renderIndicadoresMes('nx-indicadores-mes'); } catch { pinto = false; }
        dueno.renderTarjetasDelNegocio('nx-tarjetas-negocio', { sinAsistenciaNiDanos: pinto });
      });
    // IND-T4 (Ares #27702): el tablero del dueno, una tarjeta por regla de indicador_reglas con su semaforo. Mismo gate.
    // Sin el GET tablero-dueno de A no pinta nada; las reglas las aprueba Josse (GATE IND-T1), el panel no tiene lista.
    // Va ARRIBA de los accesos: medido a 375 px, debajo quedaba a ~2.100 px del inicio (dos pantallas y media).
    import('./presentation/shared/NxTableroDueno.js').then((m) => m.renderTableroDueno('nx-tablero-dueno')).catch((e) => console.warn('[inicio] no cargo el tablero del dueno', e));
    // Como nos fue ayer (Ares #26519): reporte.como_nos_fue_hoy por la API, la misma cifra que WhatsApp. Mismo gate.
    import('./presentation/shared/NxComoNosFue.js').then((m) => m.renderComoNosFue('nx-como-nos-fue')).catch((e) => console.warn('[inicio] no cargo como nos fue', e));
    // PEDIDOS-TIEMPOS-PANEL-T1 (SI de Ares #32179): cuanto tarda un pedido hoy, por GET pedidos-tiempos. Mismo gate.
    import('./presentation/shared/NxTiemposPedido.js').then((m) => m.renderTiemposPedido('nx-tiempos-pedido')).catch((e) => console.warn('[inicio] no cargo tiempos de pedido', e));
  }
  // PANEL-OPS-V2-001 T4 — KPIs de la semana (solo manager)
  if (role === 'manager') loadWeekKpis('nx-week-kpis');
  // PANEL-OPS-V3-001 T1 — Quick-Actions FAB (solo manager)
  if (role === 'manager') setupQuickActionsFab('nx-fab-root');
}

/**
 * Dashboard simplificado para rol "client" (negocio final).
 * Solo 3 tarjetas operativas (Asistencia/Kiosk, Inventario, Carrito) + 4 "Proximamente"
 * (Auditoria, Publicidad, Banco, Capacitacion) + FAB. Sin NxsWidget/KPIs/footer.
 * Asistencia apunta directo a #/marcacion (kiosk), no a la vista de resumen.
 */
function renderBusinessDashboard(container) {
  // Reset del titulo de pagina (otras vistas lo setean al render)
  const $pt = document.getElementById('nx-page-title');
  if ($pt) $pt.textContent = '';

  container.innerHTML = `
    <section class="dashboard dashboard--business">
      <h1>Nexo Panel</h1>
      <p class="subtitle">Dashboard</p>
      <div class="dashboard-grid dashboard-grid--business">
        <a href="#/marcacion" class="dash-card"><span class="dash-icon">&#128336;</span><span class="dash-label">Asistencia</span><span class="dash-count">Kiosk</span></a>
        <a href="#/inventario" class="dash-card"><span class="dash-icon">&#128203;</span><span class="dash-label">Inventario</span><span class="dash-count">Sync Desktop</span></a>
        <a href="#/carrito" class="dash-card"><span class="dash-icon">&#128722;</span><span class="dash-label">Carrito</span><span class="dash-count">Ventas</span></a>
        <div class="dash-card dash-card--disabled" aria-disabled="true"><span class="dash-icon">&#128269;</span><span class="dash-label dash-label--muted">Auditoria</span><span class="dash-coming-soon">Proximamente</span></div>
        <div class="dash-card dash-card--disabled" aria-disabled="true"><span class="dash-icon">&#128247;</span><span class="dash-label dash-label--muted">Publicidad</span><span class="dash-coming-soon">Proximamente</span></div>
        <div class="dash-card dash-card--disabled" aria-disabled="true"><span class="dash-icon">&#127974;</span><span class="dash-label dash-label--muted">Banco</span><span class="dash-coming-soon">Proximamente</span></div>
        <div class="dash-card dash-card--disabled" aria-disabled="true"><span class="dash-icon">&#127891;</span><span class="dash-label dash-label--muted">Capacitacion</span><span class="dash-coming-soon">Proximamente</span></div>
        <a href="#/argemiro-kiosk" class="dash-card"><span class="dash-icon">&#129497;</span><span class="dash-label">Argemiro</span><span class="dash-count">Compras + chat</span></a>
        <a href="#/kiosk-movimientos" class="dash-card"><span class="dash-icon">&#128230;</span><span class="dash-label">Anotar movimiento</span><span class="dash-count">Boto / dano / porciono</span></a>
        ${tarjetaAprobaciones({ tablet: true, role: AppConfig.role, permitida: true })}
      </div>
      <div id="nx-fab-root"></div>
    </section>`;
  setupQuickActionsFab('nx-fab-root');
  // APR-BOTONES-T1 (Ares #25677): el dueno entra con su negocio y cae AQUI; con el
  // menu escondido por role-client, esta tarjeta es su camino a la bandeja.
  // Solo manager: el colaborador (client) no decide lo que pide la caja.
  pintarConteoAprobaciones(container);
  // IND-T1 ARCHIVADA (decision Josse 12-ago, bitacora hotfix v2.2.301): las 5
  // tarjetas del dueno NO van en esta vista — este dashboard es la TABLET de los
  // colaboradores (rol client), no el home del propietario. El componente
  // NxIndicadoresDueno.js queda en el repo sin montar; si se retoma, va en una
  // vista de audiencia propietario y con gate de rol explicito.
  // SUJ-T3 (21-sep): de ese componente solo se monta renderTarjetasDelNegocio, en el
  // Dashboard completo (renderDashboard) con gate de rol; aqui, en la tablet, NO.
}

/** APR-BOTONES-T1: cuantas cosas esperan la decision del dueno, en la tarjeta Aprobaciones. */
async function pintarConteoAprobaciones(container) {
  const $c = container.querySelector('[data-dash-aprobaciones-conteo]');
  if (!$c) return;
  const { n, masVieja } = await resumenPorAprobar();
  if ($c.isConnected) $c.textContent = conteoEnPalabras(n, masVieja);
}

/**
 * PROMESA-ESTADO-T1 (Ares #25944): la promesa del dia en tres cifras con su criterio. Solo cuando la
 * API sirve el criterio (nexo-api 488); antes, el bloque se queda escondido ("no antes").
 */
async function pintarPromesaDelDia(container) {
  const $p = container.querySelector('[data-promesa-dia]');
  if (!$p) return;
  let p = null;
  try {
    const r = await SaludApiClient.getIndicadores();
    p = r.ok ? promesaDelDia(r.data.indicadores) : null;
  } catch { p = null; }
  if (!p || !$p.isConnected) return;
  const cifra = (n, txt, color) => `<span style="display:inline-flex;align-items:baseline;gap:5px;margin-right:14px">`
    + `<strong style="font-size:1.25rem;color:${color};font-variant-numeric:tabular-nums">${n}</strong>`
    + `<span style="font-size:.875rem;color:#8b949e">${txt}</span></span>`;
  $p.innerHTML = `<a href="#/salud" style="display:block;margin:14px 0 4px;padding:12px 14px;border:1px solid #30363d;border-radius:10px;background:#161b22;text-decoration:none">`
    + `<div style="font-size:.875rem;color:#c9d1d9;margin-bottom:6px">La promesa de hoy${p.prometidos !== null ? ' (' + p.prometidos + ' prometidos)' : ''}</div>`
    + `<div style="display:flex;flex-wrap:wrap;row-gap:4px">`
    + cifra(p.cumplidos, p.cumplidos === 1 ? 'cumplido' : 'cumplidos', '#3fb950')
    + cifra(p.entregados, p.entregados === 1 ? 'entregado' : 'entregados', '#58a6ff')
    + cifra(p.pendientes, p.pendientes === 1 ? 'pendiente' : 'pendientes', '#e3b341')
    + `</div><div data-promesa-criterio style="font-size:.875rem;color:#8b949e;margin-top:6px">${p.criterio}: cumplido es solo lo verificado; lo entregado va aparte.</div></a>`;
  $p.hidden = false;
}

/**
 * DECISIONES-ARTEFACTO-T1: la tarjeta "Lo que necesita de vos" dice las decisiones PENDIENTES
 * reales del buzon (no las respondidas). Si no se pudo leer, deja su texto: no se inventa un cero.
 */
async function pintarConteoDecisiones(container) {
  const $c = container.querySelector('[data-dash-decisiones-conteo]');
  if (!$c) return;
  const r = await JosseColaApiClient.buzon('pendiente');
  const n = pendientesReales(r.ok, r.ok ? r.filas.map(leerDecision) : null);
  if (n !== null && $c.isConnected) $c.textContent = n === 0 ? 'Nada pendiente' : (n === 1 ? '1 pendiente' : n + ' pendientes');
}

/**
 * PANEL-OPS-V3-001 T1 — atajos del inicio (Nuevo pedido, Nueva verificacion, Marcar entrada).
 * FRONT-OBSTRUCCIONES-T1 (opcion A, Ares #30261): antes era un boton flotante (+) que abria el menu y tapaba tarjetas
 * del inicio (medido 2.2.636); ahora es una fila de atajos dentro del contenido, siempre a la vista, sin menu.
 */
function setupQuickActionsFab(rootId) {
  const $root = document.getElementById(rootId);
  if (!$root) return;
  $root.innerHTML = `
    <nav class="atajos-inicio" id="nx-fab" aria-label="Atajos">
      <span class="atajos-inicio__titulo">Atajos</span>
      <a class="atajos-inicio__item" href="#/carrito"><span aria-hidden="true">&#128722;</span> Nuevo pedido</a>
      <a class="atajos-inicio__item" href="#/inventario"><span aria-hidden="true">&#128203;</span> Nueva verificacion</a>
      <a class="atajos-inicio__item" href="#/marcacion"><span aria-hidden="true">&#128336;</span> Marcar entrada</a>
    </nav>`;
}

async function loadDashCounts() {
  const { TaskApiClient }        = await import('./infrastructure/api/TaskApiClient.js');
  const { MissionApiClient }     = await import('./infrastructure/api/MissionApiClient.js');
  const { InstructionApiClient } = await import('./infrastructure/api/InstructionApiClient.js');
  const { ProjectApiClient }     = await import('./infrastructure/api/ProjectApiClient.js');
  const { UserApiClient }        = await import('./infrastructure/api/UserApiClient.js');
  const { AgentApiClient }       = await import('./infrastructure/api/AgentApiClient.js');

  const { leerContadoresDeAccesos, textoDeAcceso } = await import('./application/dashboard/contadoresVivos.js');

  // PANEL-CONTADORES-VIVOS-T1 (Ares #33023): cada numero con su palabra y pedido con esa misma definicion
  // (tareas abiertas, misiones vivas, instrucciones y proyectos activos). La regla vive en contadoresVivos.js.
  // SEC-USERS-ROL-T1 (Ares #38356): /api/v1/Users es solo de admin (nexo-api 672). El conteo se pide solo si la
  // tarjeta de Usuarios se pinto (show('#/users'): admin y dev); a los demas no se les hace un 403 en cada inicio.
  const pideUsuarios = !!document.getElementById('dc-users');
  const [accesos, users, agents] = await Promise.all([
    leerContadoresDeAccesos({
      estadisticasDeTareas: () => TaskApiClient.statistics(),
      tareasPorEstado: (estado) => TaskApiClient.list(1, 1, estado),
      misionesPorEstado: (estado) => MissionApiClient.list(1, 1, { status: estado }),
      instruccionesActivas: () => InstructionApiClient.list(1, 1, 'active'),
      proyectosActivos: () => ProjectApiClient.list(1, 1, 'active')
    }),
    pideUsuarios ? UserApiClient.list(1, 1) : Promise.resolve({ ok: false }),
    AgentApiClient.list(1, 1)
  ]);

  for (const [id, clave] of [['dc-tasks', 'tareas'], ['dc-missions', 'misiones'], ['dc-instructions', 'instrucciones'], ['dc-projects', 'proyectos']]) {
    const $el = document.getElementById(id);
    if ($el) $el.textContent = textoDeAcceso(clave, accesos[clave]);
  }

  // INICIO-CLARIDAD: sin total, "—" (antes "?").
  const { cuantos, textoConteo } = await import('./application/dashboard/conteoDeTarjeta.js');
  for (const [id, res] of [['dc-users', users], ['dc-agents', agents]]) {
    const $el = document.getElementById(id);
    if ($el && res.ok) $el.textContent = textoConteo(cuantos(res), '');
  }
}

async function loadDesktopCount() {
  try {
    const { LicenseApiClient } = await import('./infrastructure/api/LicenseApiClient.js');
    const res = await LicenseApiClient.listClients();
    const clients = res?.data?.data ?? res?.data ?? [];
    const $el = document.getElementById('dc-desktop');
    if ($el && Array.isArray(clients)) {
      const registered = clients.filter(c => (c.business_id || c.businessId) && c.active !== false);
      const now = Date.now();
      const active = registered.filter(c => {
        const exp = c.effective_end_date || c.effectiveEndDate
                 || c.subscription_limit || c.subscriptionLimit || '';
        if (!exp) return true; // sin fecha = desde suscripcion del negocio (activo)
        const d = new Date(exp);
        if (isNaN(d.getTime()) || d.getFullYear() < 2000) return true; // fecha invalida/min-date = activo
        return d > now;
      });
      $el.textContent = `${active.length} activos / ${registered.length} total`;
    }
  } catch (_) {}
}

async function loadNegociosCount() {
  try {
    const { BusinessApiClient } = await import('./infrastructure/api/BusinessApiClient.js');
    const res = await BusinessApiClient.list();
    const items = res.data?.data?.items || res.data?.items || res.data || [];
    const $el = document.getElementById('dc-negocios');
    if ($el) $el.textContent = Array.isArray(items) ? items.length : '—';

    // Conteo colaboradores activos de todos los negocios
    if (Array.isArray(items) && items.length > 0) {
      const businessIds = items.map(b => b.businessId || b.id);
      // PANEL-DASH-ABANICO (FINDING #17185): un solo recorrido para las tres tarjetas.
      loadContadoresDeNegocios(businessIds);
    }
  } catch { /* silent */ }
}

/**
 * PANEL-DASH-ABANICO (FINDING #17185): las tarjetas Colaboradores, Horarios y Asistencia salen de
 * UN solo recorrido por los negocios (3 peticiones por negocio, 4 a la vez), que se corta si el
 * Dashboard ya no esta en pantalla y guarda 5 min en la sesion. Antes eran tres recorridos en
 * serie (140 peticiones en 35 negocios) que seguian corriendo al salir: 593/min y 111 429.
 * Regla en application/dashboard/contadoresDeNegocios.js.
 */
async function loadContadoresDeNegocios(businessIds) {
  try {
    const [{ contarNegocios }, { ColaboradorApiClient }, { PlantillasApiClient }, { AsistenciaApiClient }] = await Promise.all([
      import('./application/dashboard/contadoresDeNegocios.js'),
      import('./infrastructure/api/ColaboradorApiClient.js'),
      import('./infrastructure/api/PlantillasApiClient.js'),
      import('./infrastructure/api/AsistenciaApiClient.js')
    ]);
    const $colab = document.getElementById('dc-colaboradores');
    const $horarios = document.getElementById('dc-horarios');
    const $asis = document.getElementById('dc-asistencia');
    if (!$colab && !$horarios && !$asis) return;
    const memoria = {
      get: (k) => sessionStorage.getItem(k),
      set: (k, v) => sessionStorage.setItem(k, v)
    };
    const r = await contarNegocios(businessIds, {
      colaboradores: (id) => ColaboradorApiClient.list(id, 1, 50, 'activo'),
      plantillas: (id) => PlantillasApiClient.list(id),
      hoy: (id) => AsistenciaApiClient.resumenHoy(id)
    }, {
      estaVivo: () => [$colab, $horarios, $asis].some(($e) => $e && $e.isConnected),
      memoria
    });
    if (r.cortado) return;
    if ($colab) $colab.textContent = r.activos;
    if ($horarios) $horarios.textContent = `${r.conHorario}/${r.listados}`;
    if ($asis) $asis.textContent = `${r.presentes} presente${r.presentes === 1 ? '' : 's'}`;
  } catch { /* silent */ }
}

async function loadNxsCount() {
  try {
    const { RewardService } = await import('./application/RewardService.js');
    const board = await RewardService.getLeaderboard();
    const $el = document.getElementById('dc-nxs');
    if ($el) $el.textContent = `${board.nxs} NXS`;
  } catch { /* silent */ }
}

/** NXS acumulados por skills desbloqueados (suma de nexos_reward). [PANEL-FEAT-002] */
async function loadLearningNxsSkillsBadge() {
  try {
    const { LearningApiClient } = await import('./infrastructure/api/LearningApiClient.js');
    const { getJwtUserId } = await import('./presentation/shared/jwtUserId.js');
    const uid = getJwtUserId();
    const res = await LearningApiClient.getSkillProgress(uid !== 'anon' ? uid : undefined);
    const $el = document.getElementById('nx-dash-skill-nxs-chip');
    if (!$el) return;
    if (!res.ok) {
      $el.textContent = 'NXS skills: —';
      return;
    }
    const skills = res.data?.data || [];
    let total = 0;
    for (const s of skills) {
      const answered = parseInt(s.answered, 10) || 0;
      const accuracy = parseFloat(s.accuracy) || 0;
      const required = parseInt(s.questions_required, 10) || 10;
      const reqAccuracy = parseInt(s.accuracy_required, 10) || 70;
      const unlocked = answered >= required && accuracy >= reqAccuracy;
      if (unlocked) total += parseInt(s.nexos_reward, 10) || 0;
    }
    $el.textContent = `NXS ganados (skills): ${total}`;
  } catch {
    const $el = document.getElementById('nx-dash-skill-nxs-chip');
    if ($el) $el.textContent = 'NXS skills: —';
  }
}

/**
 * COLAB-PANEL-P1 (bit #3293, auditoria colaboradores d844414/v2.2.244):
 * la card "Capacitacion" del dashboard mostraba "41 preguntas" como texto FIJO
 * en el HTML -- nunca se actualizaba, quedaba desfasada en cuanto se agregaba
 * o quitaba una pregunta del banco. Mismo patron que CapacitacionView usa para
 * su stat "Total preguntas" (limit:100, .length real).
 */
async function loadLearningQuestionsCount() {
  try {
    const { LearningApiClient } = await import('./infrastructure/api/LearningApiClient.js');
    const $el = document.getElementById('dc-learning');
    if (!$el) return;
    const res = await LearningApiClient.getQuestions({ limit: 100, random: false });
    // INICIO-CLARIDAD: la API puede traer {items}; contar asi daba "undefined preguntas".
    const { cuantos, textoConteo } = await import('./application/dashboard/conteoDeTarjeta.js');
    $el.textContent = textoConteo(cuantos(res), 'pregunta');
  } catch {
    const $el = document.getElementById('dc-learning');
    if ($el) $el.textContent = 'preguntas: —';
  }
}

/** COLAB-PANEL-P1: card "Skills" mostraba "8 skills" fijo -- mismo problema. */
async function loadSkillsCount() {
  try {
    const { LearningApiClient } = await import('./infrastructure/api/LearningApiClient.js');
    const $el = document.getElementById('dc-skills');
    if (!$el) return;
    const res = await LearningApiClient.getSkills();
    const { cuantos, textoConteo } = await import('./application/dashboard/conteoDeTarjeta.js');
    $el.textContent = textoConteo(cuantos(res), 'skill');
  } catch {
    const $el = document.getElementById('dc-skills');
    if ($el) $el.textContent = 'skills: —';
  }
}

/**
 * PANEL-OPS-V3-001 T2 — Widget KPIs con rangos + Export CSV (solo manager).
 *
 * Rangos: hoy | semana (default) | mes | custom dd-dd.
 * Cachea el ultimo rango en sessionStorage (no persistente entre logins).
 * Export CSV produce 1 archivo con fila por dia del rango.
 */

const KPI_RANGE_KEY = 'nx_week_kpis_range';

function kpiComputeRange(preset, customFrom, customTo) {
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  if (preset === 'hoy') {
    return { from: start, to: today, label: 'Hoy' };
  }
  if (preset === 'mes') {
    const from = new Date(start);
    from.setDate(from.getDate() - 29);
    return { from, to: today, label: 'Ultimo mes (30 dias)' };
  }
  if (preset === 'custom' && customFrom && customTo) {
    const from = new Date(customFrom + 'T00:00:00');
    const to = new Date(customTo + 'T23:59:59');
    return { from, to, label: `${customFrom} → ${customTo}` };
  }
  // semana (default)
  const from = new Date(start);
  from.setDate(from.getDate() - 6);
  return { from, to: today, label: 'Ultima semana (7 dias)' };
}

function kpiIsoDate(d) {
  return d.toISOString().slice(0, 10);
}

function kpiEachDay(from, to) {
  const days = [];
  const d = new Date(from);
  d.setHours(0, 0, 0, 0);
  const end = new Date(to);
  end.setHours(0, 0, 0, 0);
  while (d <= end) {
    days.push(kpiIsoDate(d));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

async function loadWeekKpis(elId) {
  const $el = document.getElementById(elId);
  if (!$el) return;
  const { getSelectedBusiness } = await import('./presentation/components/BusinessSelector.js');
  const bizId = getSelectedBusiness() || '';
  if (!bizId) {
    $el.innerHTML = `
      <div class="week-kpis">
        <div class="week-kpis__title">KPIs</div>
        <div class="week-kpis__empty">Selecciona un negocio para ver KPIs.</div>
      </div>`;
    return;
  }

  // Restaurar rango desde sessionStorage (T2)
  let savedRange = 'semana';
  let savedFrom = '';
  let savedTo = '';
  try {
    const cached = JSON.parse(sessionStorage.getItem(KPI_RANGE_KEY) || '{}');
    if (cached?.range) savedRange = cached.range;
    if (cached?.from) savedFrom = cached.from;
    if (cached?.to) savedTo = cached.to;
  } catch { /* silent */ }

  $el.innerHTML = `
    <div class="week-kpis" id="wk-root">
      <div class="week-kpis__header">
        <div class="week-kpis__title">KPIs <span class="week-kpis__range-label" id="wk-range-label"></span></div>
        <div class="week-kpis__controls">
          <select id="wk-range-select" aria-label="Rango">
            <option value="hoy">Hoy</option>
            <option value="semana">Semana</option>
            <option value="mes">Mes</option>
            <option value="custom">Personalizado</option>
          </select>
          <input type="date" id="wk-from" style="display:none" aria-label="Desde" value="${savedFrom}" />
          <input type="date" id="wk-to" style="display:none" aria-label="Hasta" value="${savedTo}" />
          <button type="button" class="ops-cta--secondary" id="wk-csv">Exportar CSV</button>
        </div>
      </div>
      <div class="week-kpis__grid">
        <a href="#/carrito" class="week-kpi" id="wk-ventas">
          <span class="week-kpi__label">Ventas</span>
          <span class="week-kpi__val" id="wk-ventas-val">…</span>
          <span class="week-kpi__hint" id="wk-ventas-hint">pedidos</span>
        </a>
        <a href="#/inventario" class="week-kpi" id="wk-verif">
          <span class="week-kpi__label">Conteos del panel</span>
          <span class="week-kpi__val" id="wk-verif-val">…</span>
          <span class="week-kpi__hint" id="wk-verif-hint">preliminares</span>
        </a>
        <a href="#/asistencia" class="week-kpi" id="wk-asist">
          <span class="week-kpi__label">Puntualidad</span>
          <span class="week-kpi__val" id="wk-asist-val">…</span>
          <span class="week-kpi__hint" id="wk-asist-hint">&mdash;</span>
        </a>
        <a href="#/inventario" class="week-kpi" id="wk-diff">
          <span class="week-kpi__label">Items con diff.</span>
          <span class="week-kpi__val" id="wk-diff-val">…</span>
          <span class="week-kpi__hint" id="wk-diff-hint">&mdash;</span>
        </a>
      </div>
    </div>`;

  const $select = document.getElementById('wk-range-select');
  const $from = document.getElementById('wk-from');
  const $to = document.getElementById('wk-to');
  const $csv = document.getElementById('wk-csv');
  $select.value = savedRange;

  const currentState = { data: null };

  const syncCustomInputs = () => {
    const isCustom = $select.value === 'custom';
    $from.style.display = isCustom ? '' : 'none';
    $to.style.display = isCustom ? '' : 'none';
  };

  const persistRange = () => {
    try {
      sessionStorage.setItem(KPI_RANGE_KEY, JSON.stringify({
        range: $select.value,
        from: $from.value,
        to: $to.value
      }));
    } catch { /* silent */ }
  };

  const refresh = async () => {
    syncCustomInputs();
    const preset = $select.value;
    if (preset === 'custom' && (!$from.value || !$to.value)) return;
    const range = kpiComputeRange(preset, $from.value, $to.value);
    const $label = document.getElementById('wk-range-label');
    if ($label) $label.textContent = `· ${range.label}`;
    persistRange();

    // Reset
    ['wk-ventas-val','wk-verif-val','wk-asist-val','wk-diff-val'].forEach(id => {
      const $x = document.getElementById(id); if ($x) $x.textContent = '…';
    });
    currentState.data = await kpiFetchByDay(bizId, range);
    kpiRenderCards(currentState.data);
  };

  $select.addEventListener('change', refresh);
  $from.addEventListener('change', refresh);
  $to.addEventListener('change', refresh);
  $csv.addEventListener('click', () => {
    if (!currentState.data) return;
    kpiExportCsv(currentState.data, bizId);
  });

  refresh();
}

async function kpiFetchByDay(bizId, range) {
  const { ApiBase } = await import('./infrastructure/api/ApiBase.js');
  const days = kpiEachDay(range.from, range.to);
  const byDay = {};
  days.forEach(d => {
    byDay[d] = { fecha: d, ventas_pedidos: 0, ventas_total_cop: 0, verificaciones: 0, confirmados_desktop: 0, entradas: 0, puntuales: 0, items_con_diff: 0 };
  });

  const inRange = (d) => d && d >= range.from && d <= range.to;

  // PANEL-CARGA-EFICIENCIA-T1: las tres consultas salen juntas. Antes cada una esperaba a la anterior (3 viajes en
  // fila, los dos primeros de 500 y 300 filas). Lo que se calcula con cada una no cambia.
  const pedidoVentas = ApiBase.get(`/api/sync/ordenes?businessId=${encodeURIComponent(bizId)}&pageSize=500`).catch(() => null);
  const pedidoVerificaciones = ApiBase.get(`/api/sync/verificacion_inventario?businessId=${encodeURIComponent(bizId)}&pageSize=300`).catch(() => null);
  const pedidoAsistencia = ApiBase.get(`/api/v1/businesses/${encodeURIComponent(bizId)}/asistencia?${new URLSearchParams({ desde: kpiIsoDate(range.from), hasta: kpiIsoDate(range.to) })}`).catch(() => null);

  // Ventas
  try {
    const res = await pedidoVentas;
    const items = res.data?.data?.items || [];
    for (const it of items) {
      const raw = it.data?.fecha || it.syncedAt;
      const d = raw ? new Date(raw) : null;
      if (!inRange(d)) continue;
      const key = kpiIsoDate(d);
      if (!byDay[key]) continue;
      byDay[key].ventas_pedidos++;
      byDay[key].ventas_total_cop += parseFloat(it.data?.total) || 0;
    }
  } catch { /* silent */ }

  // Verificaciones
  try {
    const res = await pedidoVerificaciones;
    const items = res.data?.data?.items || [];
    for (const s of items) {
      const raw = s.data?.fecha || s.syncedAt;
      const d = raw ? new Date(raw) : null;
      if (!inRange(d)) continue;
      const key = kpiIsoDate(d);
      if (!byDay[key]) continue;
      // CONTEO-HISTORIAL-ESTADOS-T1: la sesion del Desktop es lo confirmado, no una verificacion mas; sus items son
      // solo los ajustados, asi que tampoco suman "items con diferencia" (se contarian dos veces).
      if (etapaDeSesion(s) === 'confirmado') { byDay[key].confirmados_desktop++; continue; }
      byDay[key].verificaciones++;
      const list = s.data?.items || [];
      for (const row of list) {
        const dif = parseFloat(row.diferencia || 0);
        if (dif !== 0) byDay[key].items_con_diff++;
      }
    }
  } catch { /* silent */ }

  // Asistencia
  try {
    const res = await pedidoAsistencia;
    const raw = res.data?.data?.items || res.data?.data || res.data || [];
    const arr = Array.isArray(raw) ? raw : [];
    const umbral = Number(window.nxBusinessConfig?.umbral_tardanza_minutos ?? 5);
    for (const r of arr) {
      if (r.tipo !== 'entrada') continue;
      const dstr = r.fecha || r.timestamp || r.createdAt;
      if (!dstr) continue;
      const dt = new Date(dstr);
      if (!inRange(dt)) continue;
      const key = kpiIsoDate(dt);
      if (!byDay[key]) continue;
      byDay[key].entradas++;
      const minT = r.minutosTarde ?? r.minutos_tarde ?? 0;
      if (minT <= umbral) byDay[key].puntuales++;
    }
  } catch { /* silent */ }

  return { byDay, days, range };
}

function kpiRenderCards(result) {
  let pedidos = 0, ventasTotal = 0, verif = 0, confirmados = 0, diff = 0, entradas = 0, puntuales = 0;
  for (const d of result.days) {
    const row = result.byDay[d];
    pedidos += row.ventas_pedidos;
    ventasTotal += row.ventas_total_cop;
    verif += row.verificaciones;
    confirmados += row.confirmados_desktop || 0;
    diff += row.items_con_diff;
    entradas += row.entradas;
    puntuales += row.puntuales;
  }
  const fmt = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
  const $vv = document.getElementById('wk-ventas-val');
  const $vh = document.getElementById('wk-ventas-hint');
  if ($vv) $vv.textContent = String(pedidos);
  if ($vh) $vh.textContent = ventasTotal > 0 ? `pedidos · ${fmt.format(ventasTotal)}` : 'pedidos';

  const $rv = document.getElementById('wk-verif-val');
  if ($rv) $rv.textContent = String(verif);
  const $rh = document.getElementById('wk-verif-hint');
  if ($rh) $rh.textContent = `preliminares · ${confirmados} confirmado${confirmados === 1 ? '' : 's'} en el Desktop`;
  const $dv = document.getElementById('wk-diff-val');
  const $dh = document.getElementById('wk-diff-hint');
  if ($dv) $dv.textContent = String(diff);
  if ($dh) $dh.textContent = `${result.days.length} dias`;

  const $av = document.getElementById('wk-asist-val');
  const $ah = document.getElementById('wk-asist-hint');
  if (entradas === 0) {
    if ($av) $av.textContent = '—';
    if ($ah) $ah.textContent = 'sin datos';
  } else {
    const pct = Math.round(puntuales / entradas * 100);
    if ($av) $av.textContent = `${pct}%`;
    if ($ah) $ah.textContent = `${puntuales}/${entradas} a tiempo`;
  }
}

function kpiExportCsv(result, bizId) {
  const header = ['fecha', 'ventas_pedidos', 'ventas_total_cop', 'conteos_panel_preliminar', 'puntualidad_pct', 'items_con_diff', 'confirmados_desktop'];
  const rows = [header.join(',')];
  for (const d of result.days) {
    const r = result.byDay[d];
    const pct = r.entradas > 0 ? Math.round(r.puntuales / r.entradas * 100) : '';
    rows.push([
      r.fecha,
      r.ventas_pedidos,
      Math.round(r.ventas_total_cop),
      r.verificaciones,
      pct,
      r.items_con_diff,
      r.confirmados_desktop || 0
    ].join(','));
  }
  const csv = rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const stamp = `${kpiIsoDate(result.range.from)}_${kpiIsoDate(result.range.to)}`;
  a.download = `kpis_${bizId}_${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
}

const router = new Router($main);

router
  // CATALOGO art-dashboard: El home del panel. Responde: que esta pasando ahora mismo en el negocio.
  .route('#/', renderDashboard)
  // CATALOGO art-tasks: El listado de tareas del ecosistema, donde se crean, se cambian de estado y se cierran. Responde: que hay pendiente y de quien es.
  .route('#/tasks', renderTaskList)
  // CATALOGO art-missions: Las misiones vivas del ecosistema, con su prioridad y su avance. Responde: en que estamos trabajando.
  .route('#/missions', renderMissionList)
  // CATALOGO art-mission-detail: El detalle de una mision con sus tareas e instrucciones enlazadas. Responde: que falta para cerrar esta.
  .route('#/missions/:id', renderMissionDetail)
  // CATALOGO art-instruction-detail: El detalle y la edicion de una instruccion del catalogo de Argemiro. Responde: que dice exactamente esta instruccion.
  .route('#/instructions/:id', renderInstructionDetail)
  // CATALOGO art-instructions: El catalogo de instrucciones que sigue Argemiro. Responde: que sabe hacer y con que reglas.
  .route('#/instructions', renderInstructionList)
  // CATALOGO art-projects: El selector de proyectos del ecosistema. Responde: sobre cual de los proyectos estoy mirando.
  .route('#/projects', renderProjectSelector)
  // CATALOGO art-project-detail: El detalle de un proyecto con sus pestanas. Responde: que hay dentro de este proyecto.
  .route('#/projects/:key', renderProjectDetail)
  // CATALOGO art-users: La gestion de usuarios y su avance en el pensum. Responde: quien entra al panel y que ha aprendido.
  .route('#/users', renderUserList)
  // CATALOGO art-personas: todas las personas activas de todos los negocios (SUJ-T3, solo admin de plataforma). Responde: quien es quien en toda la casa.
  .route('#/personas', renderPersonasMaster)
  // SUJ-T8: la ficha unica, abierta desde Personas, Colaboradores y Usuarios con el id que cada una tiene.
  // CATALOGO art-ficha-persona-colaborador: la ficha unica de una persona, entrando desde su ficha de colaborador. Responde: como va esta persona, en una sola vista.
  .route('#/persona/colaborador/:id', renderFichaPersona)
  // CATALOGO art-ficha-persona-usuario: la ficha unica de una persona, entrando desde su usuario del panel. Responde: como va esta persona, en una sola vista.
  .route('#/persona/usuario/:id', renderFichaPersona)
  // CATALOGO art-ficha-persona: la ficha unica de una persona, entrando desde la lista de Personas. Responde: como va esta persona, en una sola vista.
  .route('#/persona/:id', renderFichaPersona)
  // CATALOGO art-ficha-agente: la ficha de un agente: lo que sabe (demostrado) separado de lo que puede (autorizado), con enlace a cada fuente. Responde: que sabe y que puede este agente.
  .route('#/agente/:code', renderFichaAgente)
  // CATALOGO art-agents: El centro de conversacion con los agentes. Responde: como le hablo a Argemiro y a los Argys.
  .route('#/agents', renderAgentList)
  // CATALOGO art-agents-dashboard: El estado live de los Argys: quien esta activo, en que mision y cuando escribio por ultima vez. Responde: estan trabajando ahora.
  .route('#/agents-dashboard', renderAgentsDashboard)
  // CATALOGO art-salud: La Sala de Maquinas: la salud del ecosistema medida, no opinada. Responde: que esta roto o envejeciendo por dentro.
  .route('#/salud', renderSalud)
  // CATALOGO art-contexto: Quien tiene llave del contexto de Josse y con que alcance. Responde: a quien le abri la puerta y hasta donde.
  .route('#/contexto', renderContexto)
  // MCP-NEXO-OAUTH-T1 (PR c): la API manda aqui con ?solicitud=<uuid>; el router busca la ruta sin la query.
  // CATALOGO art-oauth-autorizar: Donde Josse aprueba o niega que ChatGPT entre al MCP de Nexo en su nombre. Responde: quien pide entrar y con que permisos.
  .route('#/oauth/autorizar', renderOAuthAutorizar)
  // CATALOGO art-decisiones: La cola de lo que esta PARADO esperando una decision de Josse, y se destraba ahi mismo. Responde: que esta detenido por mi.
  .route('#/decisiones', renderJosseCola)
  // CATALOGO art-danos-colaborador: Danos y entradas de ajuste agrupados por colaborador y periodo. Responde: quien esta rompiendo o ajustando, y cuanto.
  .route('#/danos-colaborador', renderDanosColaborador)
  // CATALOGO art-pensum: El pensum de cada agente en cascada (agente, skill, unidad) con su estado y evidencia. Responde: que sabe hacer cada agente y que le falta demostrar.
  .route('#/pensum', renderPensum)
  // CATALOGO art-funcionalidades: Las funcionalidades que cada tipo de negocio trae encendidas, y cuales son propuesta. Responde: que tiene prendido un restaurante, una tienda o una joyeria al crearse.
  .route('#/funcionalidades', renderFuncionalidades)
  // CATALOGO art-arbol: El arbol de un proyecto como mapa: el proyecto al centro, sus grupos y cada pieza como hoja, con su estado. Responde: que vive, que falta y que no se debe usar.
  .route('#/arbol', renderArbol)
  // CATALOGO art-notificaciones: La bandeja del propietario con lo que necesita su decision. Responde: que me estan pidiendo aprobar.
  .route('#/notificaciones', renderNotificaciones)
  // CATALOGO art-kiosk-movimientos: El kiosco para anotar que paso con un insumo, desde el sitio. Responde: como registro un desperdicio o una entrada sin sentarme.
  .route('#/kiosk-movimientos', renderMovimientosKiosk)
  // CATALOGO art-banco: El banco de NXS del equipo y de cada agente. Responde: cuanto ha ganado cada quien.
  .route('#/banco', renderBancoNxs)
  // CATALOGO art-negocios: La lista de negocios clientes de Nexo Digital. Responde: a quienes le servimos.
  .route('#/negocios', renderNegociosList)
  // CATALOGO art-negocio-nuevo: El asistente de siete pasos para dar de alta un negocio. Responde: como monto un cliente nuevo completo.
  .route('#/negocios/nuevo', renderNegocioNuevoWizard)
  // CATALOGO art-negocio-detail: El detalle de un negocio: informacion, contacto, equipos y suscripcion. Responde: que se de este cliente.
  .route('#/negocios/:id', renderNegocioDetail)
  // CATALOGO art-onboard-publico: La pagina publica por la que un cliente nuevo se registra solo. Responde: como entra un cliente sin que yo lo cargue.
  .route('#/onboard/:token', renderOnboardPublic)
  // CATALOGO art-colaboradores: Los colaboradores del negocio seleccionado. Responde: quien trabaja aqui y con que permisos.
  .route('#/colaboradores', renderColaboradores)
  // CATALOGO art-horarios: Los horarios de trabajo de cada colaborador. Responde: a que hora se espera a cada uno.
  .route('#/horarios', renderHorarios)
  // CATALOGO art-asistencia: El control de asistencia: marcajes, tardanzas y jornadas. Responde: quien llego, cuando y cuanto trabajo.
  .route('#/asistencia', renderAsistencia)
  // CATALOGO art-auditoria-asistencia: La auditoria de asistencia, de solo lectura. Responde: cuadran los marcajes con lo que se pago.
  .route('#/auditoria-asistencia', renderAsistenciaAudit)
  // CATALOGO art-marcacion: El kiosco donde el colaborador marca entrada y salida con su PIN. Responde: como registro mi jornada.
  .route('#/marcacion', renderMarcacion)
  // CATALOGO art-desktop-admin: El centro de control de Nexo Desktop: version, licencia y sincronizacion. Responde: esta el Desktop al dia y hablando con la nube.
  .route('#/desktop-admin', renderDesktopAdmin)
  // CATALOGO art-inventario: El inventario del negocio: existencias, conteos y ajustes. Responde: cuanto hay de cada cosa y cuadra con lo contado.
  .route('#/inventario', renderInventario)
  // CATALOGO art-carrito: El carrito de ventas con los productos que sincroniza el Desktop. Responde: como registro una venta desde el panel.
  .route('#/carrito', renderCarrito)
  // CATALOGO art-presupuestos: El asistente que arma presupuestos de obra a partir del catalogo. Responde: cuanto cuesta este trabajo.
  .route('#/presupuestos', renderPresupuestos)
  // CATALOGO art-items-estandar: El editor del item estandar de Maderinco por seccion y linea. Responde: como se nombra cada item recurrente y como creo una variante.
  .route('#/items-estandar', renderItemEstandar)
  // CATALOGO art-auditoria: La auditoria de documentos con lectura automatica de facturas. Responde: dice el papel lo mismo que el sistema.
  .route('#/auditoria', renderAuditoria)
  // CATALOGO art-publicidad: La gestion de campanas y piezas de publicidad. Responde: que estamos publicando y donde.
  .route('#/publicidad', renderPublicidad)
  // CATALOGO art-notes: Las notas del ecosistema, enlazables a misiones. Responde: que anotamos y a que trabajo pertenece.
  .route('#/notes', renderNotes)
  // CATALOGO art-knowledge: El conocimiento de Argemiro: instrucciones y RAG en un solo sitio. Responde: de donde saca Argemiro lo que contesta.
  .route('#/knowledge', renderKnowledge)
  // CATALOGO art-items: El catalogo de objetos del ecosistema: workflows, componentes y artefactos. Responde: que tenemos construido ya.
  .route('#/items', renderItemList)
  // CATALOGO art-capacitacion: La administracion del sistema de aprendizaje: banco de preguntas y avance. Responde: que se esta ensenando y quien va aprendiendo.
  .route('#/capacitacion', renderCapacitacion)
  // CATALOGO art-skills: Las skills y el pensum, con el progreso de cada usuario. Responde: que tiene que saber cada quien y cuanto lleva.
  .route('#/skills', renderSkills)
  // CATALOGO art-agent-quality: La memoria persistente de Argemiro, inspeccionable. Responde: que recuerda Argemiro de cada conversacion.
  .route('#/agent-quality', renderAgentQuality)
  // CATALOGO art-equipos: El catalogo de equipos en dos niveles: modelos y unidades. Responde: que maquinas tenemos y cuales estan en uso.
  .route('#/equipos', renderEquiposCatalog)
  // CATALOGO art-compras: Las compras y los movimientos de inventario: desperdicios, danos, devoluciones y aumentos. Responde: que entro, que salio y por que.
  .route('#/compras', renderCompras)
  // CATALOGO art-argemiro-kiosk: El kiosco de Argemiro para que el colaborador le pida cosas desde el sitio. Responde: como le hablo a Argemiro sin computador.
  .route('#/argemiro-kiosk', renderArgemiroKiosk)
  // CATALOGO art-saldos: Los saldos acumulados por medio de pago. Responde: cuanta plata hay y donde esta.
  .route('#/saldos', renderSaldos)
  // CATALOGO art-informes: Los tres informes del dueno con exportar a Excel (INF-T4). Responde: en que se fue la plata, cuanto le pague a un proveedor y cuanto gane cada mes.
  .route('#/informes', renderInformes)
  // CATALOGO art-nexo-pagos: El control de cobros de Nexo Digital a sus clientes. Responde: quien nos debe y cuanto.
  .route('#/nexo-pagos', renderNexoPagos)
  // CATALOGO art-ingresos: Los ingresos, espejo del formulario de Desktop. Responde: cuanto entro y de donde.
  .route('#/ingresos', renderIngresos)
  // CATALOGO art-egresos: Los egresos, con paridad con el formulario de Desktop. Responde: cuanto salio y en que.
  .route('#/egresos', renderEgresos)
  // CATALOGO art-creditos: Los abonos de credito de los clientes. Responde: quien esta pagando su fiado y cuanto falta.
  .route('#/creditos', renderCreditos)
  // CATALOGO art-system: El estado del sistema: versiones de API y panel, entorno y conexion. Responde: contra que estoy hablando y en que version.
  .route('#/system', renderSystemInfo)
  .fallback(renderDashboard);

function isPublicRoute() {
  const hash = window.location.hash || '';
  return /^#\/onboard\//.test(hash);
}

/**
 * PANEL-OPS-V4-001 T2 — Bypasea auth gate para rutas publicas (#/onboard/*).
 * Renderiza la vista publica directamente en #nx-main sin setupNavBar / selector.
 */
function showPublic() {
  $gate.hidden = true;
  $app.hidden = false;
  // Ocultar navbar + barra contextual para que sea full-width publico
  const $nav = document.querySelector('.topbar, #nx-topbar, nav.topbar');
  if ($nav) $nav.style.display = 'none';
  router.start();
  window.addEventListener('hashchange', () => {
    if (!isPublicRoute()) {
      // Al salir de la ruta publica, re-arrancar flujo normal (fuerza reload)
      window.location.reload();
    }
  });
}

async function boot() {
  if (isPublicRoute()) {
    showPublic();
    return;
  }
  if (AppConfig.isAuthenticated) {
    const valid = await AuthService.validateSession();
    if (valid) {
      showApp();
    } else {
      showGate();
    }
  } else {
    showGate();
  }
}

boot();
